"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { StockLogo } from "@/components/stock-logo";
import { StockPillList, type PillStock } from "@/components/stock-pill-list";
import { FREE_SHOWCASE_TICKERS } from "@/lib/config";
import type { ScoreSummary } from "@/lib/data/score-summary";

const SHOWCASE_SYMBOLS = FREE_SHOWCASE_TICKERS.map((t) => t.symbol);

/**
 * Herausgelöst aus SearchHero (Erik 2026-08-14: "direkt unter der Suchleiste das
 * kostenlos starten... und direkt darunter dann die 4 Aktien") - damit die Reihenfolge
 * im Hero frei bestimmbar ist (Suche → CTA → Gratis-Aktien), statt fest an SearchHero
 * gekoppelt zu sein. SearchHero (nur noch `compact`/Header-Kontext) zeigt diese Zeile
 * seitdem nicht mehr selbst - einziger Aufrufer war ohnehin die Startseite.
 *
 * `variant="inline"` (Landingpage-Feedback 2026-08-17, Punkt 8): kompakte Fassung ohne
 * einzelne Chip-Rahmen/Namen/Score - nur ein kurzes Label + reine Logo-Reihe, gedacht als
 * rechte Haelfte der neuen zusammengesetzten Such-/Gratis-Aktien-Leiste (siehe
 * app/page.tsx). `variant="chips"` (Default) bleibt die bisherige volle Pill-Darstellung
 * fuer andere/spaetere Einsatzorte.
 */
export function FreeTickerPills({ variant = "chips" }: { variant?: "chips" | "inline" }) {
  const [showcaseStocks, setShowcaseStocks] = useState<PillStock[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/recent-scores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbols: SHOWCASE_SYMBOLS }),
      signal: controller.signal,
    })
      .then((res) => (res.ok ? (res.json() as Promise<ScoreSummary[]>) : []))
      .then((results) => {
        const bySymbol = new Map(results.map((r) => [r.symbol, r]));
        setShowcaseStocks(
          SHOWCASE_SYMBOLS.map((symbol) => bySymbol.get(symbol)).filter(
            (s): s is ScoreSummary => s !== undefined,
          ),
        );
      })
      .catch(() => {
        // Fallback bleibt die reine Text-Chip-Zeile unten - kein Fehlerzustand noetig.
      });

    return () => controller.abort();
  }, []);

  if (variant === "inline") {
    // Logos laden unabhaengig vom Score-Fetch oben (StockLogo hat einen eigenen
    // Parqet-Fallback, sobald `logoUrl` fehlt) - kein Lade/Fallback-Unterschied noetig
    // wie bei der `chips`-Variante, da hier ohnehin kein Score angezeigt wird.
    return (
      <div className="flex h-full w-full items-center justify-center gap-1.5 px-3 sm:justify-start sm:gap-1.5 sm:px-2">
        {/* Vorher komplett `hidden` unter `sm` (nur die 4 Logos ohne jede Erklaerung auf
            Mobile) UND `text-muted-foreground` (schwacher Kontrast) - Landingpage-Feedback
            2026-08-17 #2, Punkt 3: "ein wenig besser zu erkennen machen, welche 4 Aktien
            man kostenlos ohne Account einsehen kann". Kurzfassung ab jetzt IMMER sichtbar
            (auch Mobile), volle Fassung ab `sm` in kraeftigerer Farbe/Gewicht statt der
            vorherigen blassen `text-xs`. Nochmals vergroessert (text-xs -> text-sm,
            max-w 9.5rem -> 11rem) - Landingpage-Feedback Runde 3, 2026-08-17, Punkt 9:
            "noch ein klein wenig größer". max-w wieder auf 8rem reduziert + Padding/Gaps
            gestrafft (px-4->px-2, gap-2.5->gap-1.5, Logo-Gap 1.5->1) - Landingpage-Feedback
            Runde 4, 2026-08-17, Punkt 6: das 4. Logo (SAP) lief durch die Vergroesserung
            aus Runde 3 rechts aus dem sichtbaren/`overflow-hidden`-Rahmen raus (live per
            getBoundingClientRect nachgemessen: 36px Overlauf) - Text bleibt bei `text-sm`
            (nur die Spaltenbreite schrumpft, nicht die Schriftgroesse), umbricht dadurch auf
            eine dritte Zeile statt zwei, passt aber weiterhin in die `h-16`-Zeile. */}
        <span className="shrink-0 text-sm leading-tight font-medium text-ink-secondary sm:hidden">
          {FREE_SHOWCASE_TICKERS.length} gratis
        </span>
        <span className="hidden max-w-[8rem] shrink-0 text-sm leading-tight font-medium text-ink-secondary sm:block">
          {/* Ein einziger Template-String statt {expr} + JSX-Text nebeneinander - Letzteres
              hat die Leerstelle nach der Zahl verschluckt (React fuegt zwischen den beiden
              Kindknoten nur einen leeren Hydrations-Kommentar ein, keinen echten Space). */}
          {`${FREE_SHOWCASE_TICKERS.length} Aktien kostenlos & ohne Account einsehbar`}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {FREE_SHOWCASE_TICKERS.map((chip) => {
            const loaded = showcaseStocks.find((s) => s.symbol === chip.symbol);
            return (
              <Link
                key={chip.symbol}
                href={`/${encodeURIComponent(chip.symbol)}`}
                title={chip.name}
                className="transition-transform hover:-translate-y-0.5"
              >
                {/* size 24 -> 32 (naechstgroesserer erlaubter StockLogo-Wert), gleicher
                    Punkt 9 wie oben. */}
                <StockLogo symbol={chip.symbol} logoUrl={loaded?.logoUrl} size={32} />
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  if (showcaseStocks.length > 0) {
    return <StockPillList label="Kostenlos & ohne Account einsehbar" stocks={showcaseStocks} compact />;
  }

  // Fallback bis die Scores geladen sind (oder falls der Ladevorgang fehlschlaegt) -
  // dieselben Aktien, nur ohne Logo/Score, damit die Zeile nie leer bleibt.
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {FREE_SHOWCASE_TICKERS.map((chip) => (
        <span
          key={chip.symbol}
          className="rounded-full border border-hairline bg-card px-3.5 py-1.5 text-xs text-muted-foreground"
        >
          {chip.name}
        </span>
      ))}
    </div>
  );
}
